import * as i0 from '@angular/core';
import { inject, Injectable, InjectionToken, ChangeDetectorRef, Pipe, Injector, NgModule } from '@angular/core';
import { NEVER, of } from 'rxjs';
import { DOCUMENT } from '@angular/common';
export * from '@simpl/element-ng/translate-types';

/**
 * This var provides access to the global scope object based on the current runtime.
 * On Browser its is the window, on Node.js it is global.
 *
 * Whenever possible, inject(DOCUMENT) is preferred over this approach.
 */
let globalScope;
// we are either running Browser or on NodeJS
if (typeof window !== 'undefined') {
    globalScope = window;
}
else if (typeof global !== 'undefined') {
    globalScope = global;
}

/**
 * @returns The culture language code of the browser, e.g. "en-US".
 */
const getBrowserCultureLanguage = () => window?.navigator?.languages?.[0] ?? window?.navigator?.language;
/**
 * @returns The language code of the browser, e.g. "en".
 */
const getBrowserLanguage = () => getBrowserCultureLanguage()?.split(/-|_/)[0];
/**
 * Wrapper around an actual translation framework which is meant to be used internally by Element.
 * Applications must not use this service.
 *
 * @internal
 */
class SiTranslateService {
    translationChange$ = NEVER;
    documentRef = inject(DOCUMENT);
    prevent$LocalizeInit;
    /**
     * If the underlying translation library supports switching the language
     * and/or updating the translation texts, this observable will emit.
     * There is no initial emit.
     */
    get translationChange() {
        return this.translationChange$;
    }
    setDocumentLanguage(lang) {
        this.documentRef?.documentElement?.setAttribute('lang', lang);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateService, decorators: [{
            type: Injectable
        }] });

const arrayToRecord = (keys) => keys.reduce((acc, cur) => {
    acc[cur] = cur;
    return acc;
}, {});
/**
 * Pass through implementation of the {@link SiTranslateService} which is used as a default.
 *
 * @internal
 */
class SiNoTranslateService extends SiTranslateService {
    get currentLanguage() {
        return 'en';
    }
    get availableLanguages() {
        return ['en'];
    }
    set availableLanguages(lang) { }
    setCurrentLanguage(lang) {
        return of();
    }
    getDefaultLanguage() {
        return 'en';
    }
    setDefaultLanguage(lang) { }
    translate(keys, _params) {
        // "as any" due to Typescript not being able to handle type guards in combination with generics
        return typeof keys === 'string' ? keys : arrayToRecord(keys);
    }
    translateSync(keys, params) {
        return this.translate(keys, params);
    }
    translateAsync(keys, params) {
        return of(this.translate(keys, params));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNoTranslateService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNoTranslateService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNoTranslateService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const SI_TRANSLATABLE_VALUES = new InjectionToken('si-element-translatable-values');

class SiTranslatableService {
    translatableValues;
    isNoTranslate;
    siTranslateService;
    constructor() {
        this.siTranslateService =
            inject(SiTranslateService, { optional: true }) ?? inject(SiNoTranslateService);
        const translateValues = inject(SI_TRANSLATABLE_VALUES, { optional: true });
        this.translatableValues = translateValues?.reduce((a, b) => ({ ...a, ...b })) ?? {};
        this.isNoTranslate = this.siTranslateService instanceof SiNoTranslateService;
    }
    resolveText(key, originalText) {
        if (this.siTranslateService.setTranslation) {
            this.siTranslateService.setTranslation(key, originalText);
        }
        return this.translatableValues[key] ?? (this.isNoTranslate ? originalText : key);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslatableService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslatableService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslatableService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

// an alternative implementation for $localize meant to be used for translation frameworks other than @angular/localize
const $localize = (strings, ...expressions) => {
    if (strings.length !== 1) {
        throw new Error('$localize calls using @simpl/element-ng do not support parameter interpolation');
    }
    const firstPart = strings[0];
    const [, key, value] = /:.*?@@(.*?):(.*)/.exec(firstPart);
    return Zone.current.get('siResolveLocalize')(key, value);
};
const siResolveLocalize = (key, value) => inject(SiTranslatableService).resolveText(key, value);
/**
 * Initializes the SiMPL version of $localize. Can be called multiple times.
 */
const initSiLocalize = () => {
    Zone.current._properties.siResolveLocalize = siResolveLocalize;
    globalScope.$localize = $localize;
};

/**
 * Provide this class to build a custom translation service.
 *
 * @internal
 */
class SiTranslateServiceBuilder {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateServiceBuilder, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateServiceBuilder });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateServiceBuilder, decorators: [{
            type: Injectable
        }] });

/**
 * Always returns the same instance of {@link SiNoTranslateService}
 *
 * @internal
 */
class SiNoTranslateServiceBuilder extends SiTranslateServiceBuilder {
    siNoTranslateService = inject(SiNoTranslateService);
    buildService() {
        return this.siNoTranslateService;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNoTranslateServiceBuilder, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNoTranslateServiceBuilder, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiNoTranslateServiceBuilder, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * Translates keys by using the {@link SiTranslateService}.
 * Within Element this pipe should be used instead of ngx-translates `translate` pipe.
 * Outside Element, this pipe should NOT be used.
 *
 * @internal
 */
// eslint-disable-next-line @angular-eslint/no-pipe-impure
class SiTranslatePipe {
    lastKeyParams;
    value = '';
    subscription;
    siTranslateService = inject(SiTranslateService);
    cdRef = inject(ChangeDetectorRef);
    /**
     * Method which is called on any data passed to the pipe.
     * Called by Angular, should not be called directly.
     */
    transform(key, params) {
        if (!key) {
            return '';
        }
        const currentKeyParams = params ? `${key}-${JSON.stringify(params)}` : key;
        if (this.lastKeyParams === currentKeyParams) {
            return this.value;
        }
        this.subscription?.unsubscribe();
        const translate = this.siTranslateService.translate(key, params);
        if (typeof translate === 'string') {
            this.updateValue(currentKeyParams, translate);
        }
        else {
            this.subscription = translate.subscribe(value => this.updateValue(currentKeyParams, value));
        }
        return this.value;
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.subscription = undefined;
    }
    updateValue(currentKeyParams, value) {
        this.lastKeyParams = currentKeyParams;
        this.value = value;
        this.cdRef.markForCheck();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiTranslatePipe, name: "translate", pure: false });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslatePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'translate', pure: false }]
        }] });

/**
 * This provides declares SiTranslatePipe and provides a respective SiTranslateService.
 * It should be used internally of Element but NOT by any application
 *
 * @internal
 */
class SiTranslateModule {
    constructor() {
        const translateService = inject(SiTranslateService);
        if (!translateService.prevent$LocalizeInit) {
            initSiLocalize();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateModule, declarations: [SiTranslatePipe], exports: [SiTranslatePipe] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateModule, providers: [
            /* This is needed for ngx-translate when using the isolated mode for lazy child routes.
             * In that case a new TranslateService is created by ngx-translate which also needs to be used
             * by SiMPL components within that route. The Builder can be used to check if a new service is available
             * and then provide a corresponding SiTranslateService.
             */
            {
                provide: SiTranslateService,
                useFactory: (injector, noTranslateBuilder) => injector.get(SiTranslateServiceBuilder, noTranslateBuilder).buildService(injector),
                deps: [Injector, SiNoTranslateServiceBuilder]
            }
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiTranslateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiTranslatePipe],
                    exports: [SiTranslatePipe],
                    providers: [
                        /* This is needed for ngx-translate when using the isolated mode for lazy child routes.
                         * In that case a new TranslateService is created by ngx-translate which also needs to be used
                         * by SiMPL components within that route. The Builder can be used to check if a new service is available
                         * and then provide a corresponding SiTranslateService.
                         */
                        {
                            provide: SiTranslateService,
                            useFactory: (injector, noTranslateBuilder) => injector.get(SiTranslateServiceBuilder, noTranslateBuilder).buildService(injector),
                            deps: [Injector, SiNoTranslateServiceBuilder]
                        }
                    ]
                }]
        }], ctorParameters: function () { return []; } });

const provideSiTranslatableOverrides = values => ({
    useValue: values,
    multi: true,
    provide: SI_TRANSLATABLE_VALUES
});

/* eslint-disable */

/**
 * Generated bundle index. Do not edit.
 */

export { SiNoTranslateService, SiTranslateModule, SiTranslatePipe, SiTranslateService, SiTranslateServiceBuilder, getBrowserCultureLanguage, getBrowserLanguage, initSiLocalize, provideSiTranslatableOverrides };
